/**
 * MediaPreviewDialog — the ONE way media opens in this console.
 *
 * Media used to be handed straight to the browser: a chat thumbnail was an
 * `<a target="_blank">` onto the S3 signed URL, a document header was a link,
 * a library tile was not clickable at all. That throws the operator out of the
 * app — and for anything the browser will not render inline, straight into a
 * download they never asked for. Neither is the right outcome for "let me see
 * what the customer sent".
 *
 * So every media affordance now opens this dialog instead. Images, video,
 * audio and PDFs render inline; anything else says so honestly and offers a
 * download. The download is fetched into a Blob and clicked from an object
 * URL, so even THAT stays in-app — no navigation, no tab. The single
 * `target="_blank"` in this file is the CORS fallback for the download action
 * only, and never a path for viewing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFileOutlined,
} from '@mui/icons-material';

export type MediaPreviewKind = 'image' | 'video' | 'audio' | 'document' | 'other';

export interface MediaPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` while the caller has nothing selected — the dialog renders empty
   *  rather than the caller having to unmount it. */
  url: string | null;
  mimeType?: string;
  /** Explicit override. Leave it off for documents whose exact type is only
   *  known from the mime/extension: `document` here means "renderable as a
   *  PDF", and forcing it onto a .docx would show an empty frame. */
  kind?: MediaPreviewKind;
  fileName?: string;
  caption?: string;
}

/** Extension → kind, for URLs that arrive without a usable mime type (most
 *  presigned links do, since the mime lives on the S3 object, not the link). */
const EXT_KIND: Record<string, MediaPreviewKind> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  svg: 'image',
  avif: 'image',
  heic: 'image',
  mp4: 'video',
  webm: 'video',
  mov: 'video',
  m4v: 'video',
  '3gp': 'video',
  '3gpp': 'video',
  mkv: 'video',
  avi: 'video',
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  oga: 'audio',
  opus: 'audio',
  m4a: 'audio',
  aac: 'audio',
  amr: 'audio',
  pdf: 'document',
};

/** Strips the query string and fragment a presigned URL always carries before
 *  looking at the path — `…/invoice.pdf?X-Amz-Signature=…` must still read as
 *  a PDF, and `.pdf?X-Amz-Algorithm` must not read as an extension. */
function pathOf(url: string): string {
  const withoutHash = url.split('#')[0] || '';
  const withoutQuery = withoutHash.split('?')[0] || '';
  try {
    return decodeURIComponent(withoutQuery);
  } catch {
    return withoutQuery;
  }
}

function extensionOf(name: string): string {
  const base = pathOf(name);
  const dot = base.lastIndexOf('.');
  if (dot < 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

function baseNameOf(url: string): string {
  const segments = pathOf(url).split('/');
  return segments[segments.length - 1] || '';
}

export function resolveMediaKind(
  kind: MediaPreviewKind | undefined,
  mimeType: string | undefined,
  fileName: string | undefined,
  url: string | null,
): MediaPreviewKind {
  if (kind) return kind;
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'document';
  const ext = extensionOf(fileName || '') || extensionOf(url || '');
  return (ext && EXT_KIND[ext]) || 'other';
}

export default function MediaPreviewDialog({
  open,
  onClose,
  url,
  mimeType,
  kind,
  fileName,
  caption,
}: MediaPreviewDialogProps) {
  const resolvedKind = useMemo(
    () => resolveMediaKind(kind, mimeType, fileName, url),
    [kind, mimeType, fileName, url],
  );
  const title = fileName || (url ? baseNameOf(url) : '') || 'Preview';

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [downloading, setDownloading] = useState(false);
  /** Set once a Blob download has been refused (a signed URL without the CORS
   *  headers). Only then may an anchor take over, and only for downloading. */
  const [downloadBlocked, setDownloadBlocked] = useState(false);

  useEffect(() => {
    // A new file (or a reopen) starts from scratch: the previous file's error
    // must not colour this one, and 'other' has nothing to wait for.
    setStatus(resolvedKind === 'other' ? 'ready' : 'loading');
    setDownloadBlocked(false);
    setDownloading(false);
  }, [url, open, resolvedKind]);

  const handleDownload = useCallback(async () => {
    if (!url) return;
    setDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = title;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Revoked on the next tick: some browsers abort a save that is still
      // being handed off when its object URL disappears synchronously.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      setDownloadBlocked(true);
    } finally {
      setDownloading(false);
    }
  }, [url, title]);

  const showSkeleton = status === 'loading' && resolvedKind !== 'other';

  const renderBody = () => {
    if (!url) return null;

    if (status === 'error') {
      return (
        <Box sx={{ py: 4 }}>
          <Alert severity="error">This file could not be loaded.</Alert>
        </Box>
      );
    }

    if (resolvedKind === 'image') {
      return (
        <Box
          component="img"
          src={url}
          alt={caption || title}
          onLoad={() => setStatus('ready')}
          onError={() => setStatus('error')}
          sx={{
            display: showSkeleton ? 'none' : 'block',
            maxWidth: '100%',
            maxHeight: '85vh',
            width: 'auto',
            margin: '0 auto',
            objectFit: 'contain',
          }}
        />
      );
    }

    if (resolvedKind === 'video') {
      return (
        <Box
          component="video"
          src={url}
          controls
          autoPlay={false}
          playsInline
          preload="metadata"
          onLoadedMetadata={() => setStatus('ready')}
          onError={() => setStatus('error')}
          sx={{
            display: showSkeleton ? 'none' : 'block',
            width: '100%',
            maxHeight: '85vh',
            margin: '0 auto',
            outline: 'none',
          }}
        />
      );
    }

    if (resolvedKind === 'audio') {
      return (
        <Box sx={{ py: 4, display: showSkeleton ? 'none' : 'flex', justifyContent: 'center' }}>
          <Box
            component="audio"
            src={url}
            controls
            preload="metadata"
            onLoadedMetadata={() => setStatus('ready')}
            onError={() => setStatus('error')}
            sx={{ width: '100%', maxWidth: 520 }}
          />
        </Box>
      );
    }

    if (resolvedKind === 'document') {
      return (
        <Box
          sx={{
            display: showSkeleton ? 'none' : 'block',
            width: '100%',
            height: '80vh',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <iframe
            src={`${url}#toolbar=1`}
            title={title}
            onLoad={() => setStatus('ready')}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      );
    }

    // Everything we cannot render — docx, xlsx, zip and friends. Saying so is
    // more useful than an empty frame that silently downloads.
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 320 }}>
        <InsertDriveFileOutlined sx={{ fontSize: 56, color: 'text.disabled' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview not available for this file type
        </Typography>
        <DownloadAction
          blocked={downloadBlocked}
          busy={downloading}
          onDownload={handleDownload}
          url={url}
          fileName={title}
          variant="contained"
        />
      </Stack>
    );
  };

  const isVisual = resolvedKind === 'image' || resolvedKind === 'video';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-label={`Preview ${title}`}>
      <DialogTitle sx={{ py: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0 }} noWrap title={title}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <DownloadAction
              blocked={downloadBlocked}
              busy={downloading}
              onDownload={handleDownload}
              url={url}
              fileName={title}
              variant="text"
            />
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose} aria-label="Close preview">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          ...(isVisual ? { bgcolor: '#0f172a' } : {}),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {showSkeleton && (
          <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 1, width: '100%' }} />
        )}
        {renderBody()}
      </DialogContent>
      {(caption || downloadBlocked) && (
        <Box sx={{ px: 3, py: 1.25 }}>
          {caption && (
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
          )}
          {downloadBlocked && (
            <Typography variant="caption" color="text.disabled" display="block">
              Opens in new tab
            </Typography>
          )}
        </Box>
      )}
    </Dialog>
  );
}

/**
 * The download control. Normally an in-app Blob save; only after the fetch has
 * actually been refused does it become an anchor, and even then it downloads
 * rather than views.
 */
function DownloadAction({
  blocked,
  busy,
  onDownload,
  url,
  fileName,
  variant,
}: {
  blocked: boolean;
  busy: boolean;
  onDownload: () => void;
  url: string | null;
  fileName: string;
  variant: 'text' | 'contained';
}) {
  if (blocked && url) {
    return (
      <Button
        size="small"
        variant={variant}
        startIcon={<DownloadIcon />}
        component="a"
        href={url}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
      >
        Download
      </Button>
    );
  }
  return (
    <Button
      size="small"
      variant={variant}
      startIcon={<DownloadIcon />}
      onClick={onDownload}
      disabled={!url || busy}
    >
      Download
    </Button>
  );
}
